import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Column, Header, HeaderGroup, Table, flexRender } from '@tanstack/react-table'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import Input from '../input'
import { ColumnMetaType } from './TableRow'
import { useIsMounted } from '../../../common/hooks/useIsMounted'

export function TableHeader<T>(props: { table: Table<T>; headerClass: string }) {
    const { table, headerClass } = props
    const headerGroups = table.getHeaderGroups()

    return (
        <thead className={`border-b border-gray-300 pb-6 sticky top-0 z-10 bg-white ${headerClass}`}>
            {headerGroups.map((headerGroup, headerGroupIndex) => {
                const isLastHeaderRow = headerGroups.length - 1 === headerGroupIndex
                return <TableHeaderRow key={headerGroup.id} headerGroup={headerGroup} isLastHeaderRow={isLastHeaderRow} />
            })}
        </thead>
    )
}

function TableHeaderRow<T>(props: { headerGroup: HeaderGroup<T>; isLastHeaderRow: boolean }) {
    const { headerGroup, isLastHeaderRow } = props

    return (
        <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
                <TableHeaderColumn key={header.id} header={header} isLastHeaderRow={isLastHeaderRow} />
            ))}
        </tr>
    )
}

function TableHeaderColumn<T>(props: { header: Header<T, unknown>; isLastHeaderRow: boolean }) {
    const { header, isLastHeaderRow } = props
    const filter = (header.column.columnDef.meta as ColumnMetaType<T> | undefined)?.filter
    const portalElement = filter?.filterPortalId ? document.getElementById(filter.filterPortalId) : null

    return (
        <th
            key={header.id}
            className={`${isLastHeaderRow ? 'pb-2.5' : 'pb-2.5'} text-xl font-bold text-left leading-none max-tablet:text-base`}
            colSpan={header.colSpan}
            style={(header.column.columnDef.meta as ColumnMetaType<T> | undefined)?.style}
        >
            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            {renderFilter()}
        </th>
    )

    function renderFilter() {
        if (!filter) return null

        if (filter.filterPortalId) {
            return portalElement ? createPortal(<Filter column={header.column} label={filter.label} />, portalElement) : null
        }

        return (
            <div>
                <Filter column={header.column} label={filter.label} />
            </div>
        )
    }
}

function Filter<T>(props: { column: Column<T, unknown>; label: string }) {
    const { column, label } = props
    const columnFilterValue = column.getFilterValue()

    return (
        <DebouncedInput
            type="text"
            value={(columnFilterValue ?? '') as string}
            onChange={(value) => column.setFilterValue(value)}
            label={label}
            className="w-36 border shadow-sm rounded-sm"
            list={column.id + 'list'}
        />
    )
}

export function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    label,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
    label: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = React.useState(initialValue)
    const isMounted = useIsMounted()

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        if (!isMounted && !value) {
            return
        }

        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <Input label={label} style={{ position: 'relative' }}>
            <input {...props} className="text-input" value={value} onChange={(e) => setValue(e.target.value)} />
            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', top: '38px', right: '12px' }} />
        </Input>
    )
}
